import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import {
  Axios,
  BaseForm as Form,
  Checkbox,
  ConditionWrapper,
  DataTable,
  Grid,
  Table,
  Segment,
  Label,
  Input,
  Dropdown,
} from 'component/base'
import { Custom } from 'component/pieces'
import { getEnv } from 'config'

export const KumiaiKoho = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
  } = props

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => ({}), [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data, row => _.find(selectedIds, id => row.id === id))
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get(
      '/api/staff/yumekukan/yumekukan_kumiai_koho/all',
      {
        params: { ...condition },
      }
    )
    setLoading(false)
    if (error) return
    setData(data)
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
      />
    ),
    [history, handleChangeSelect]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          defaultCondition={defaultCondition}
          condition={condition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElement}
          enableSidebar={false}
          compact
        >
          <DataTable
            name='yumekukan_kumiai_koho'
            size='mini'
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeader}
            tableRow={TableRowWithProps}
            data={data}
            bottomControl={<></>}
            defaultSortColumnName='keisai_start_datetime'
            defaultSortDirection='descending'
            selectedIds={selectedIds}
          />
        </ConditionWrapper>
      </Segment>
    </>
  )
})

KumiaiKoho.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

KumiaiKoho.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
}

const TableHeader = props => {
  const { column, direction, withOrder, handleSort } = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '8.0rem' }}
        sorted={column === 'yumekukan_kumiai_koho_kbn' ? direction : null}
        onClick={() => handleSort('yumekukan_kumiai_koho_kbn')}
      >
        カテゴリー
      </Table.HeaderCell>
      <Table.HeaderCell
        style={{ width: '30.0rem' }}
        sorted={column === 'kenmei' ? direction : null}
        onClick={() => handleSort('kenmei')}
      >
        タイトル
      </Table.HeaderCell>
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'keisai_start_datetime' ? direction : null}
        onClick={() => handleSort('keisai_start_datetime')}
      >
        掲載開始日
      </Table.HeaderCell>
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'name' ? direction : null}
        onClick={() => handleSort('name')}
      >
        登録者
      </Table.HeaderCell>
      <Table.HeaderCell
        style={{ width: '15.0rem' }}
        sorted={column === 'datetime' ? direction : null}
        onClick={() => handleSort('datetime')}
      >
        登録日
      </Table.HeaderCell>
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect } = props || {}

  const {
    id,
    yumekukan_kumiai_koho_kbn_name,
    kenmei,
    keisai_start_datetime,
    datetime,
    name,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={id} />
        }
      />
      <Table.Cell
        style={{ width: '8.0rem' }}
        content={yumekukan_kumiai_koho_kbn_name}
      />
      <Table.Cell style={{ width: '30.0rem' }} content={kenmei} />
      <Table.Cell
        style={{ width: '15.0rem' }}
        content={keisai_start_datetime}
      />
      <Table.Cell style={{ width: '15.0rem' }} content={name} />
      <Table.Cell style={{ width: '15.0rem' }} content={datetime} />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { condition } = props

  const { kenmei, from_datetime, to_datetime, yumekukan_kumiai_koho_kbn } =
    condition || {}

  return (
    <>
      <Grid.Column width={7}>
        <Form.Group inline>
          <Form.Textbox.DateTime
            name='from_datetime'
            value={from_datetime}
            inputAs={Input}
            width={8}
            label={<Label content='登録日' />}
          />
          <Form.Field width={1}>
            <span>〜</span>
          </Form.Field>
          <Form.Textbox.DateTime
            name='to_datetime'
            value={to_datetime}
            inputAs={Input}
            width={7}
          />
        </Form.Group>
      </Grid.Column>
      <Grid.Column width={5}>
        <Form.Textbox.Standard
          inputAs={Input}
          value={kenmei}
          label='タイトル'
          name='kenmei'
        />
      </Grid.Column>
      <Grid.Column width={3}>
        <Custom.Dropdown.YumekukanKumiaiKohoKbn
          inputAs={Dropdown}
          value={yumekukan_kumiai_koho_kbn}
          label='カテゴリー'
          name='yumekukan_kumiai_koho_kbn'
          clearable
          fluid
        />
      </Grid.Column>
    </>
  )
}
